import React, { useRef, useState, useImperativeHandle, forwardRef, useEffect, useCallback } from 'react';
import { Send, Camera, X, Mic, Square, Trash2, Link2, Loader2, Play, Pause } from 'lucide-react';
import type { UnifiedMessage } from '@/types/unified-messaging';
import { useAppTranslations } from '@/hooks/useAppTranslations';

export interface UnifiedMessageInputHandle {
  focus: () => void;
}

interface UnifiedMessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onPhotoSelect: (files: FileList | null) => void;
  replyTo: UnifiedMessage | null;
  onCancelReply: () => void;
  selectedPhotos: File[];
  uploading: boolean;
  sending: boolean;
  disabled: boolean;
  isOnline: boolean;
  uploadProgress?: number;
  // Voice recording
  isRecording?: boolean;
  recordingDuration?: number;
  audioBlob?: Blob | null;
  audioUrl?: string | null;
  voiceError?: string | null;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onCancelRecording?: () => void;
  onSendVoice?: () => void;
  // Video
  onVideoSelect?: (file: File) => void;
  onVideoLinkSend?: (url: string) => void;
  videoUploading?: boolean;
  videoUploadProgress?: number;
  // Cursor position tracking (for @mention autocomplete)
  onCursorChange?: (pos: number) => void;
  // Channel name for placeholder (e.g. "general" → "Message #general")
  channelName?: string;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const PREVIEW_BAR_COUNT = 28;

function previewWaveHeights(count: number): number[] {
  // Fixed decorative heights for the pre-send preview
  const pattern = [40, 55, 70, 85, 60, 90, 50, 75, 65, 80, 45, 95, 55, 70, 60, 85, 50, 75, 40, 90, 65, 80, 55, 70, 45, 85, 60, 75];
  return Array.from({ length: count }, (_, i) => pattern[i % pattern.length]);
}

function VoicePreviewPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(0);
  const bars = previewWaveHeights(PREVIEW_BAR_COUNT);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    setElapsed(a.currentTime);
    if (!a.paused) rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onMeta = () => setTotal(isFinite(a.duration) ? a.duration : 0);
    const onEnded = () => { setIsPlaying(false); setIsLoading(false); setElapsed(0); };
    const onPlaying = () => { setIsLoading(false); setIsPlaying(true); rafRef.current = requestAnimationFrame(tick); };
    const onWaiting = () => { setIsLoading(true); };
    const onCanPlay = () => { setIsLoading(false); };
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnded);
    a.addEventListener('playing', onPlaying);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('canplay', onCanPlay);
    return () => {
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('playing', onPlaying);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('canplay', onCanPlay);
      cancelAnimationFrame(rafRef.current);
    };
  }, [tick]);

  const toggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      setIsLoading(true);
      a.play().catch(() => setIsLoading(false));
    } else {
      a.pause();
      setIsPlaying(false);
      setIsLoading(false);
      cancelAnimationFrame(rafRef.current);
    }
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  const progress = total > 0 ? elapsed / total : 0;
  const filledBars = Math.round(progress * PREVIEW_BAR_COUNT);

  return (
    <div className="flex-1 flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
      <audio ref={audioRef} src={src} preload="auto" />
      <button
        onClick={toggle}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); toggle(e); }}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform relative"
        style={{ background: 'rgba(42,68,148,0.1)' }}
      >
        {isLoading ? (
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="#2A4494" strokeOpacity="0.25" strokeWidth="2" />
            <path d="M8 2a6 6 0 0 1 6 6" stroke="#2A4494" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : isPlaying
          ? <Pause className="w-3.5 h-3.5" style={{ color: '#2A4494' }} />
          : <Play className="w-3.5 h-3.5 ml-0.5" style={{ color: '#2A4494' }} />
        }
      </button>
      <div className="flex items-center gap-px flex-1" style={{ height: 28 }}>
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-full transition-colors duration-75"
            style={{
              height: `${h}%`,
              background: i < filledBars ? '#2A4494' : 'rgba(42,68,148,0.2)',
            }}
          />
        ))}
      </div>
      <span className="flex-shrink-0 text-[11px] tabular-nums text-gray-400">
        {isPlaying ? fmt(elapsed) : fmt(total)}
      </span>
    </div>
  );
}

export const UnifiedMessageInput = forwardRef<UnifiedMessageInputHandle, UnifiedMessageInputProps>(
  ({
    value,
    onChange,
    onSend,
    onPhotoSelect,
    replyTo,
    onCancelReply,
    selectedPhotos,
    uploading,
    sending,
    disabled,
    isOnline,
    uploadProgress,
    isRecording = false,
    recordingDuration = 0,
    audioBlob = null,
    audioUrl = null,
    voiceError = null,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    onSendVoice,
    onVideoSelect,
    onVideoLinkSend,
    videoUploading = false,
    videoUploadProgress,
    onCursorChange,
    channelName,
  }, ref) => {
    const { t } = useAppTranslations();
    const messageInputRef = useRef<HTMLTextAreaElement>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const [showVideoLinkInput, setShowVideoLinkInput] = useState(false);
    const [videoLink, setVideoLink] = useState('');

    // Auto-resize textarea to fit content (up to 4 lines)
    const adjustTextareaHeight = useCallback(() => {
      const textarea = messageInputRef.current;
      if (textarea) {
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        // Calculate line height (approx 24px per line)
        const lineHeight = 24;
        const maxLines = 4;
        const maxHeight = lineHeight * maxLines;
        // Set height to scrollHeight but cap at maxHeight
        textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      }
    }, []);

    // Adjust height when value changes
    useEffect(() => {
      adjustTextareaHeight();
    }, [value, adjustTextareaHeight]);

    const hasVideoSupport = !!onVideoSelect || !!onVideoLinkSend;

    const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && onVideoSelect) {
        onVideoSelect(file);
      }
      e.target.value = '';
    };

    // Combined handler for photo/video selection - routes based on file type
    const handleMediaSelect = (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const imageFiles: File[] = [];
      const videoFile = Array.from(files).find(f => f.type.startsWith('video/'));

      // If there's a video file, handle it separately (only one video at a time)
      if (videoFile && onVideoSelect) {
        onVideoSelect(videoFile);
        return;
      }

      // Otherwise, collect image files and pass to photo handler
      Array.from(files).forEach(file => {
        if (file.type.startsWith('image/')) {
          imageFiles.push(file);
        }
      });

      if (imageFiles.length > 0) {
        // Create a new FileList-like object
        const dt = new DataTransfer();
        imageFiles.forEach(f => dt.items.add(f));
        onPhotoSelect(dt.files);
      }
    };

    const handleVideoLinkSubmit = () => {
      const url = videoLink.trim();
      if (url && onVideoLinkSend) {
        onVideoLinkSend(url);
        setVideoLink('');
        setShowVideoLinkInput(false);
      }
    };

    useImperativeHandle(ref, () => ({
      focus: () => {
        messageInputRef.current?.focus();
      }
    }));

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    };

    const hasVoiceSupport = !!onStartRecording;

    // Recording in progress UI
    if (isRecording) {
      return (
        <div className="p-4 safe-area-bottom" style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', borderTop: '1px solid rgba(15,23,42,0.08)' }}>
          <div className="flex items-center space-x-3">
            {/* Cancel button */}
            <button
              onClick={onCancelRecording}
              className="p-3 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all duration-200 active:scale-95"
            >
              <Trash2 className="h-5 w-5" />
            </button>

            {/* Recording indicator */}
            <div className="flex-1 flex items-center space-x-3 px-4 py-3 bg-red-50 border border-red-200 rounded-full">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-red-600">Recording</span>
              <span className="text-sm text-red-500 font-mono">{formatDuration(recordingDuration)}</span>
            </div>

            {/* Stop button */}
            <button
              onClick={onStopRecording}
              className="p-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <Square className="h-5 w-5 fill-current" />
            </button>
          </div>
        </div>
      );
    }

    // Recorded audio preview UI (ready to send)
    if (audioBlob && audioUrl) {
      return (
        <div className="p-4 safe-area-bottom" style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', borderTop: '1px solid rgba(15,23,42,0.08)' }}>
          <div className="flex items-center space-x-3">
            {/* Cancel/discard */}
            <button
              onClick={onCancelRecording}
              className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200 active:scale-95"
            >
              <Trash2 className="h-5 w-5" />
            </button>

            {/* Audio preview */}
            <VoicePreviewPlayer src={audioUrl} />

            {/* Send */}
            <button
              onClick={onSendVoice}
              disabled={uploading || sending || disabled}
              className="p-3 bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white rounded-full hover:from-[#1E3270] hover:to-[#2A4494] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95 disabled:transform-none"
            >
              {(uploading || sending) ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>

        </div>
      );
    }

    // Default: normal input
    return (
      <>
        {/* Voice error */}
        {voiceError && (
          <div className="bg-red-50 px-4 py-2 border-t border-red-200">
            <p className="text-xs text-red-600">{voiceError}</p>
          </div>
        )}

        {/* Reply preview */}
        {replyTo && (
          <div
            className="px-4 py-3 animate-slide-up"
            style={{
              background: 'rgba(255,255,255,0.90)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(15,23,42,0.08)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #52B788 0%, #1B4332 100%)' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-emerald-700">
                    {t.messaging.replyingTo.replace('{name}', replyTo.sender?.display_name || 'Unknown')}
                  </p>
                  <p className="text-sm text-slate-600 truncate">{replyTo.content}</p>
                </div>
              </div>
              <button
                onClick={onCancelReply}
                className="h-7 w-7 tap-target inline-flex items-center justify-center rounded-lg ml-2 transition-colors hover:bg-red-50 shrink-0"
                style={{ border: '1px solid rgba(15,23,42,0.08)' }}
              >
                <X className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </div>
          </div>
        )}

        {/* Message input */}
        <div
          className="p-3 safe-area-bottom"
          style={{
            background: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderTop: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset',
          }}
        >
          <div className="flex items-center gap-2">
            {/* Media upload button */}
            <button
              onClick={() => photoInputRef.current?.click()}
              disabled={videoUploading}
              className="h-10 w-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50"
              style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 4px rgba(15,23,42,0.06)' }}
            >
              {videoUploading ? (
                <Loader2 className="h-5 w-5 text-slate-500 animate-spin" />
              ) : (
                <Camera className="h-5 w-5 text-slate-500" />
              )}
            </button>

            {/* Hidden file input */}
            <input
              ref={photoInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={(e) => {
                handleMediaSelect(e.target.files);
                e.target.value = '';
              }}
              className="hidden"
            />

            <div className="flex-1 relative">
              <textarea
                ref={messageInputRef}
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  onCursorChange?.(e.target.selectionStart ?? 0);
                }}
                onSelect={(e) => onCursorChange?.((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                onKeyDown={handleKeyPress}
                placeholder={!isOnline ? t.messaging.messageWillBeSentOffline : channelName ? `Message #${channelName}` : t.messaging.typeMessage}
                className="w-full px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none resize-none overflow-hidden rounded-2xl transition-all duration-200"
                style={{
                  minHeight: '42px',
                  maxHeight: '96px',
                  background: 'rgba(255,255,255,0.85)',
                  border: '1px solid rgba(15,23,42,0.10)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 4px rgba(15,23,42,0.06)',
                }}
                rows={1}
                disabled={disabled}
              />
              {!isOnline && (
                <div className="absolute right-3 top-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                </div>
              )}
            </div>

            {/* Mic / Send — cross-fade, both always in DOM */}
            <div className="relative h-10 w-10 flex-shrink-0">
              {/* Mic button — visible when empty + voice supported */}
              {hasVoiceSupport && (
                <button
                  onClick={onStartRecording}
                  disabled={disabled || !isOnline}
                  className="absolute inset-0 flex items-center justify-center rounded-xl transition-all duration-200 active:scale-95 disabled:cursor-not-allowed"
                  style={{
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(15,23,42,0.08)',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 4px rgba(15,23,42,0.06)',
                    opacity: (!value.trim() && selectedPhotos.length === 0) ? 1 : 0,
                    transform: (!value.trim() && selectedPhotos.length === 0) ? 'scale(1)' : 'scale(0.7)',
                    pointerEvents: (!value.trim() && selectedPhotos.length === 0) ? 'auto' : 'none',
                    transition: 'opacity 200ms ease, transform 200ms ease',
                  }}
                >
                  <Mic className="h-5 w-5 text-slate-500" />
                </button>
              )}
              {/* Send button — visible when has content or no voice support */}
              <button
                onClick={onSend}
                disabled={(!value.trim() && selectedPhotos.length === 0) || uploading || sending || disabled}
                className="absolute inset-0 flex items-center justify-center rounded-full text-white disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
                  boxShadow: '0 2px 8px rgba(42,68,148,0.3)',
                  opacity: (hasVoiceSupport && !value.trim() && selectedPhotos.length === 0) ? 0 : 1,
                  transform: (hasVoiceSupport && !value.trim() && selectedPhotos.length === 0) ? 'scale(0.7)' : 'scale(1)',
                  pointerEvents: (hasVoiceSupport && !value.trim() && selectedPhotos.length === 0) ? 'none' : 'auto',
                  transition: 'opacity 200ms ease, transform 200ms ease, box-shadow 200ms ease',
                }}
              >
                {(uploading || sending) ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>


          {/* Video link input */}
          {showVideoLinkInput && (
            <div className="mt-3 flex items-center gap-2 animate-fade-in">
              <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                type="url"
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') handleVideoLinkSubmit(); }}
                placeholder="Paste YouTube or Vimeo link..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-[#2A4494] focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleVideoLinkSubmit}
                disabled={!videoLink.trim()}
                className="p-2 tap-target inline-flex items-center justify-center bg-gradient-to-r from-[#2A4494] to-[#1E3270] text-white rounded-full disabled:opacity-50 transition-all shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
              <button
                onClick={() => { setShowVideoLinkInput(false); setVideoLink(''); }}
                className="p-2 tap-target inline-flex items-center justify-center text-gray-400 hover:text-red-500 rounded-full shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </>
    );
  }
);

UnifiedMessageInput.displayName = 'UnifiedMessageInput';
